import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode, MutableRefObject } from "react";
import {
  QueryClient,
  QueryClientContext,
  QueryClientProvider,
  useQueryClient,
} from "@tanstack/react-query";
import {
  PostMarkupSection,
  emptyMarkupValue,
  formatMarkupAge,
} from "@dialogueconsulting/sked-preview";
import type {
  MarkupValue,
  MarkupPendingDraft,
} from "@dialogueconsulting/sked-preview";
import { trackEventWithUser } from "libs/analytics";
import { MarkupMediaDialog } from "./MarkupMediaDialog";
import { Button } from "components/ui/button";
import { Switch } from "components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "components/ui/select";
import { hasExternalApprovals } from "utils/feature-gates";
import { useAdminPanelToggle } from "utils/feature-toggle-utils";
import { handleError } from "utils/handleError";
import collabApi from "api/collaboration";
import { queryKeys } from "api/query-keys";
import type { User } from "features/user";
import type { ColComment } from "../types";
import { upsertCollaborationCommentInCache } from "../collaboration-queries";
import type { CollaborationChatViewAdapter } from "../CollaborationChat/CollaborationChatView";
import { CollaborationInputLazy } from "../CollaborationChat/collaboration-input.lazy";
import { CollaborationChatView } from "../CollaborationChat/CollaborationChatView";
import { useCollaborationChatQuery } from "../CollaborationChat/useCollaborationChatQuery";
import {
  chronologicalMarkupComments,
  markupPostSnapshot,
  markupReviewerName,
} from "./markup-model";
import type { MarkupPostSnapshot, MarkupPreviewPost } from "./markup-model";
import {
  formatDateSafe,
  UserMessageContentV2,
} from "../CollaborationChat/hooks/useCollaborationMessage";
import { MarkupList } from "./MarkupList";
import { registerPostMarkupLeaveGuard } from "./post-markup-leave-guards";

export type PostReviewTab = "preview" | "activity" | "markup";
export interface PostMarkupController {
  enabled: boolean;
  canWrite: boolean;
  activeTab: PostReviewTab;
  setActiveTab: (tab: PostReviewTab) => void;
  hasUnsentChanges: boolean;
  requestLeave: (action: () => void) => void;
  openMarkup: (commentId?: string, annotationId?: string) => void;
  renderMarkup: (initialMediaId?: string) => ReactNode;
  renderActivity: (variant?: "modal" | "drawer") => ReactNode;
  post: MarkupPreviewPost | null | undefined;
  resolve: (comment: ColComment, resolved: boolean) => Promise<void>;
  isResolving: boolean;
}
function ActivityPane({
  adapter,
  variant,
  channelId,
  containers,
  onVisibilityChange,
}: {
  adapter: CollaborationChatViewAdapter;
  variant: "modal" | "drawer";
  channelId: string;
  containers: MutableRefObject<HTMLDivElement[]>;
  onVisibilityChange: (visible: boolean) => void;
}) {
  const localRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    onVisibilityChange(true);
    return () => onVisibilityChange(false);
  }, [onVisibilityChange]);
  const sharedRef =
    adapter.messagesContainerRef as MutableRefObject<HTMLDivElement | null>;
  useLayoutEffect(() => {
    const node = localRef.current;
    if (!node) return;
    containers.current.push(node);
    sharedRef.current = node;
    return () => {
      containers.current = containers.current.filter((item) => item !== node);
      sharedRef.current = containers.current.at(-1) || null;
    };
  }, [containers, sharedRef]);
  return (
    <CollaborationChatView
      {...adapter}
      messagesContainerRef={localRef}
      resetKey={channelId}
      variant={variant}
    />
  );
}

const PostMarkupContext = createContext<PostMarkupController | null>(null);
function ReviewerAvatar({
  name,
  image,
  external,
}: {
  name: string;
  image?: string;
  external?: boolean;
}) {
  return (
    <span className="relative flex size-5 shrink-0 items-center justify-center rounded-full bg-neutral-300 text-[8px] font-semibold leading-3 text-white">
      {image ? (
        <img
          alt=""
          src={image}
          className="size-full rounded-full object-cover"
        />
      ) : (
        name
          .split(/\s+/)
          .slice(0, 2)
          .map((part) => part[0])
          .join("")
      )}
      {external && (
        <span className="absolute -bottom-0.5 -right-0.5 grid size-2.5 place-items-center rounded-sm bg-yellow-500 text-[6px]">
          <i className="fa-regular fa-arrow-left" aria-hidden="true" />
        </span>
      )}
    </span>
  );
}
export const useOptionalPostMarkup = () => useContext(PostMarkupContext);
export function usePostMarkup() {
  const context = useOptionalPostMarkup();
  if (!context) throw new Error("Post markup requires a PostMarkupProvider");
  return context;
}
export interface PostMarkupProviderProps {
  postId: string;
  channelId: string;
  postState: string;
  user: User;
  post: MarkupPreviewPost | null | undefined;
  /** Persisted media, before pending edits in a post form. */
  savedPost?: MarkupPreviewPost | null;
  isNewPost?: boolean;
  disabled?: boolean;
  initialTab?: PostReviewTab;
  /** Changes for each explicit host request, including repeated requests for the same tab. */
  tabRequestId?: number;
  children: ReactNode;
}
export function PostMarkupProvider(props: PostMarkupProviderProps) {
  const annotationsEnabled = useAdminPanelToggle("enableApprovalAnnotations");
  // The body always runs its hooks so that flipping the admin flag or the
  // entitlement never changes the element type above `children` (a remount
  // would drop an open editor's local state). Those hooks need React Query;
  // a host mounted without a QueryClientProvider (legacy surfaces, isolated
  // unit tests) gets an inert local client instead of a crash.
  const hostQueryClient = useContext(QueryClientContext);
  const [fallbackQueryClient] = useState(() =>
    hostQueryClient ? null : new QueryClient()
  );
  const body = (
    <PostMarkupProviderBody
      {...props}
      active={annotationsEnabled && hasExternalApprovals(props.user)}
    />
  );
  if (fallbackQueryClient) {
    return (
      <QueryClientProvider client={fallbackQueryClient}>
        {body}
      </QueryClientProvider>
    );
  }
  return body;
}

function PostMarkupProviderBody({
  postId,
  channelId,
  postState,
  user,
  post,
  savedPost,
  isNewPost = false,
  disabled = false,
  initialTab = "preview",
  tabRequestId,
  active,
  children,
}: PostMarkupProviderProps & { active: boolean }) {
  const enabled = active;
  const canLoad =
    enabled && !isNewPost && Boolean(channelId && postId && user?.userId);
  const canWrite = enabled && canLoad && !disabled;
  const [activeTab, setActiveTabState] = useState<PostReviewTab>(initialTab);
  const activeTabRef = useRef(initialTab);
  const setActiveTab = useCallback((tab: PostReviewTab) => {
    activeTabRef.current = tab;
    setActiveTabState(tab);
  }, []);
  const trackMarkup = (eventName: string, metadata = {}) => {
    if (!postId || isNewPost) return;
    trackEventWithUser(
      eventName,
      { context: "internal", post_id: postId, ...metadata },
      user
    );
  };
  const changeTab = (tab: PostReviewTab) => {
    if (tab === activeTabRef.current) return;
    // The editor's shared tab subscription can echo this change synchronously.
    setActiveTab(tab);
    if (tab === "preview" || tab === "markup") {
      trackMarkup("posts-markup-switch", { view: tab });
    }
  };
  const [visibleActivityPanes, setVisibleActivityPanes] = useState(0);
  const onActivityVisibilityChange = useCallback((visible: boolean) => {
    setVisibleActivityPanes((count) => count + (visible ? 1 : -1));
  }, []);
  const { adapter, comments } = useCollaborationChatQuery({
    channelId,
    postId,
    postState,
    user,
    disabled,
    enabled: canLoad,
    markRead: activeTab === "markup" || visibleActivityPanes > 0,
  });
  const queryClient = useQueryClient();
  const activityContainers = useRef<HTMLDivElement[]>([]);
  const batches = useMemo(
    () => chronologicalMarkupComments(comments),
    [comments]
  );
  const [selectedCommentId, setSelectedCommentId] = useState<string | null>(
    null
  );
  const selectedComment =
    batches.find((item) => item._id === selectedCommentId) ?? batches.at(-1);
  const [value, setValue] = useState<MarkupValue>(emptyMarkupValue);
  const [draftSnapshot, setDraftSnapshot] = useState<MarkupPostSnapshot | null>(
    null
  );
  const [isAdding, setIsAdding] = useState(false);
  const [pendingDraft, setPendingDraft] = useState<MarkupPendingDraft>({
    draft: null,
    comment: "",
  });
  const hasPendingPin = Boolean(pendingDraft.draft);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [editItemId, setEditItemId] = useState<string | null>(null);
  const [internal, setInternal] = useState(true);
  const canSendExternally =
    user.role !== "collaborator" ||
    user.preferences?.canCreateExternalComments !== false;
  const sendInternally = internal || !canSendExternally;
  const [sending, setSending] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [pendingLeave, setPendingLeave] = useState<(() => void) | null>(null);
  const [rendererKey, setRendererKey] = useState(0);
  const identity = `${postId}:${channelId}`;
  const identityRef = useRef(identity);
  const hasItems = value.annotations.length + value.highlights.length > 0;
  const hasUnsentChanges =
    hasItems || hasPendingPin || Boolean(value.generalFeedback.trim());
  const snapshot = useMemo(() => markupPostSnapshot(post), [post]);
  const savedSnapshot = useMemo(
    () => markupPostSnapshot(savedPost),
    [savedPost]
  );
  const matchesSavedMedia = (candidate: typeof snapshot) =>
    candidate.media.length === savedSnapshot.media.length &&
    candidate.media.every(
      (item, index) =>
        item.id === savedSnapshot.media[index]?.id &&
        Boolean(item.isVideo) === Boolean(savedSnapshot.media[index]?.isVideo)
    );
  const hasUnsavedMedia =
    savedPost !== undefined && !matchesSavedMedia(snapshot);
  const hasOutdatedDraftMedia =
    savedPost !== undefined &&
    Boolean(
      draftSnapshot &&
      (value.annotations.length > 0 || hasPendingPin) &&
      !matchesSavedMedia(draftSnapshot)
    );
  const mediaNeedsSaving = hasUnsavedMedia || hasOutdatedDraftMedia;

  useEffect(() => {
    // A canceled first pin no longer owns a snapshot. The next change must
    // capture the current caption and media, including edits made in between.
    if (!hasUnsentChanges) setDraftSnapshot(null);
  }, [hasUnsentChanges]);
  const reviewerName =
    [user.firstName, user.lastName].filter(Boolean).join(" ") || user.userName;
  const resetDraft = useCallback(() => {
    setValue(emptyMarkupValue());
    setDraftSnapshot(null);
    setIsAdding(false);
    setPendingDraft({ draft: null, comment: "" });
    setSelectedItemId(null);
    setEditItemId(null);
    setInternal(true);
    setSendError(null);
    setRendererKey((key) => key + 1);
  }, []);
  useEffect(() => {
    if (identityRef.current === identity) return;
    identityRef.current = identity;
    resetDraft();
    setSelectedCommentId(null);
    setActiveTab(initialTab);
  }, [identity, initialTab, resetDraft, setActiveTab]);
  // A host can ask for a different tab on the same post, e.g. an external
  // "Comment" launcher opening Activity for the post already on screen.
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab, tabRequestId, setActiveTab]);
  useEffect(() => {
    if (!hasUnsentChanges) return;
    const beforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [hasUnsentChanges]);
  const requestLeave = useCallback(
    (action: () => void) => {
      if (sending) return;
      if (hasUnsentChanges) setPendingLeave(() => action);
      else action();
    },
    [hasUnsentChanges, sending]
  );
  useEffect(
    () => registerPostMarkupLeaveGuard(postId, requestLeave),
    [postId, requestLeave]
  );
  const openMarkup = (commentId?: string, annotationId?: string) => {
    requestLeave(() => {
      resetDraft();
      if (commentId) setSelectedCommentId(commentId);
      setSelectedItemId(annotationId ?? null);
      changeTab("markup");
    });
  };
  const cacheComment = (comment: ColComment) => {
    upsertCollaborationCommentInCache(
      queryClient,
      channelId,
      comment,
      user.userId
    );
    void queryClient.invalidateQueries({
      queryKey: queryKeys.collaboration.comments(channelId),
    });
  };
  const resolve = async (comment: ColComment, resolved: boolean) => {
    if (!canWrite || isResolving) return;
    setIsResolving(true);
    try {
      const { data } = await collabApi.setMarkupResolution(
        channelId,
        comment._id,
        resolved
      );
      cacheComment(data);
      trackEventWithUser(
        "posts-markup-resolve",
        { action: resolved ? "resolve" : "reopen", post_id: postId },
        user
      );
    } catch (error) {
      handleError(error, "Could not update annotations. Please try again.");
    } finally {
      setIsResolving(false);
    }
  };
  const send = async () => {
    if (!canWrite || mediaNeedsSaving || sending || !hasItems || hasPendingPin)
      return;
    setSending(true);
    setSendError(null);
    trackMarkup("posts-markup-submit", {
      pin_count: value.annotations.length,
      highlight_count: value.highlights.length,
    });
    try {
      const { data } = await collabApi.addComment(channelId, {
        body: value.generalFeedback.trim(),
        visibility: sendInternally ? "INTERNAL" : "EXTERNAL",
        metadata: {
          post: { postId },
          postType: postState.toLowerCase(),
          markup: {
            version: 1,
            captionSnapshot: (draftSnapshot || snapshot).caption,
            annotations: value.annotations,
            highlights: value.highlights,
          },
        },
      });
      cacheComment(data);
      setSelectedCommentId(data._id);
      resetDraft();
    } catch (error) {
      const response = (
        error as {
          response?: { data?: { message?: unknown; error?: unknown } };
        }
      )?.response?.data;
      const detail = response?.message || response?.error;
      setSendError(
        typeof detail === "string"
          ? detail
          : "Your annotations weren’t sent. Please try again."
      );
      handleError(error, "Could not send annotations.");
    } finally {
      setSending(false);
    }
  };
  const startAdding = () =>
    requestLeave(() => {
      resetDraft();
      setIsAdding(true);
    });
  const isDraft = isAdding || !selectedComment;
  const renderMarkup = (initialMediaId?: string) => {
    if (!enabled) return null;
    if (isNewPost)
      return (
        <div className="p-6 text-center text-sm text-neutral-700">
          You can add annotations to your post or draft after it’s created.
        </div>
      );
    if (adapter.isLoading)
      return (
        <div role="status" className="p-4 text-sm">
          Loading annotations…
        </div>
      );
    const current = isDraft
      ? value
      : {
          generalFeedback: selectedComment?.body || "",
          annotations: selectedComment?.metadata?.markup?.annotations || [],
          highlights: selectedComment?.metadata?.markup?.highlights || [],
        };
    const renderSnapshot = isDraft ? draftSnapshot || snapshot : snapshot;
    const updateValue = (next: MarkupValue) => {
      if (!draftSnapshot) setDraftSnapshot(snapshot);
      setValue(next);
      setIsAdding(true);
    };
    const selectedReviewer = selectedComment
      ? markupReviewerName(selectedComment, adapter.collaborators, user)
      : reviewerName;
    return (
      <div
        className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto bg-blue-25 p-4 font-inter text-sm font-normal leading-5 text-neutral-800"
        data-testid="post-markup-pane"
      >
        {batches.length > 0 && (
          <div className="flex gap-2">
            {isDraft ? (
              <div className="flex h-8 flex-1 items-center rounded-lg border border-neutral-100 bg-white px-3 font-medium">
                Add new annotations
              </div>
            ) : (
              <Select
                size="sm"
                value={selectedComment?._id}
                onValueChange={(id) => {
                  setSelectedCommentId(id);
                  setSelectedItemId(null);
                }}
              >
                <SelectTrigger
                  aria-label="Annotation feedback"
                  className="h-8 min-w-0 flex-1 rounded-lg px-[11px] text-sm font-normal"
                >
                  <div className="flex min-w-0 items-center gap-2">
                    <ReviewerAvatar
                      name={selectedReviewer}
                      image={
                        adapter.collaborators.find(
                          (item) => item._id === selectedComment?.createdBy
                        )?.profileImage
                      }
                      external={!selectedComment?.createdBy}
                    />
                    <span className="truncate">
                      <span className="font-medium">{selectedReviewer}</span>{" "}
                      <span className="text-neutral-600">
                        -{" "}
                        {selectedComment?.createdAt &&
                          formatDateSafe(
                            selectedComment.createdAt,
                            "EEE d MMM h:mm aaa",
                            user.timezone
                          )}
                      </span>
                    </span>
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {[...batches].reverse().map((comment) => (
                    <SelectItem
                      key={comment._id}
                      value={comment._id}
                      className="font-inter [&>span:last-child]:min-w-0"
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        <ReviewerAvatar
                          name={markupReviewerName(
                            comment,
                            adapter.collaborators,
                            user
                          )}
                          image={
                            adapter.collaborators.find(
                              (item) => item._id === comment.createdBy
                            )?.profileImage
                          }
                          external={!comment.createdBy}
                        />
                        <span className="truncate">
                          {markupReviewerName(
                            comment,
                            adapter.collaborators,
                            user
                          )}{" "}
                          <span className="font-normal text-neutral-600">
                            -{" "}
                            {comment.createdAt &&
                              formatDateSafe(
                                comment.createdAt,
                                "EEE d MMM h:mm aaa",
                                user.timezone
                              )}
                            {comment.metadata?.markupResolution?.resolved
                              ? " · Resolved"
                              : ""}
                          </span>
                        </span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button
              type="button"
              size="sm"
              variant={isDraft ? "outline" : "default"}
              disabled={!canWrite || sending}
              className="size-8 shrink-0 rounded-lg p-0"
              title={isDraft ? "Cancel annotations" : "Add new annotations"}
              aria-label={
                isDraft ? "Cancel annotations" : "Add new annotations"
              }
              onClick={isDraft ? () => requestLeave(resetDraft) : startAdding}
            >
              <i
                aria-hidden="true"
                className={
                  isDraft ? "fa-regular fa-xmark" : "fa-regular fa-plus"
                }
              />
            </Button>
          </div>
        )}
        {isDraft && mediaNeedsSaving && (
          <div className="rounded-lg bg-blue-50 p-3">
            <p role="status" className="m-0">
              {hasUnsavedMedia
                ? "Save your media changes before adding annotations."
                : "The saved media has changed. Cancel these annotations and add them again to the saved media."}
            </p>
            {hasPendingPin && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => requestLeave(resetDraft)}
              >
                Discard draft
              </Button>
            )}
          </div>
        )}
        {isDraft && !hasItems && !mediaNeedsSaving && (
          <div className="flex gap-3 rounded-lg bg-blue-50 p-2">
            <span className="w-0.5 self-stretch rounded bg-blue-500" />
            <div className="flex flex-1 items-center gap-2 py-1">
              <i
                aria-hidden="true"
                className="fa-regular fa-circle-info flex size-5 shrink-0 items-center justify-center text-base text-blue-500"
              />
              <span className="font-medium">
                Click anywhere on the media to place an annotation or select
                text to highlight.
              </span>
            </div>
          </div>
        )}
        <PostMarkupSection
          key={rendererKey}
          expandedMediaDialog={MarkupMediaDialog}
          media={renderSnapshot.media}
          caption={renderSnapshot.caption}
          account={renderSnapshot.account}
          captionSnapshot={
            isDraft
              ? undefined
              : selectedComment?.metadata?.markup?.captionSnapshot
          }
          disabled={!canWrite || sending || (isDraft && mediaNeedsSaving)}
          suspended={activeTab !== "markup"}
          readOnly={!isDraft}
          reviewerName={isDraft ? reviewerName : selectedReviewer}
          reviewerIsSelf={isDraft || selectedComment?.createdBy === user.userId}
          reviewerIsExternal={!isDraft && !selectedComment?.createdBy}
          reviewerAvatarUrl={
            adapter.collaborators.find(
              (item) => item._id === selectedComment?.createdBy
            )?.profileImage
          }
          reviewerDateLabel={
            !isDraft ? formatMarkupAge(selectedComment?.createdAt) : undefined
          }
          value={current}
          onChange={updateValue}
          onAnnotationAdd={(annotation_type) =>
            trackMarkup("posts-markup-add-annotation", { annotation_type })
          }
          onExpandedMediaOpen={() => trackMarkup("posts-markup-lightbox-open")}
          pendingDraft={pendingDraft}
          onPendingDraftChange={(next) => {
            setPendingDraft(next);
            if (next.draft) {
              setDraftSnapshot((previous) => previous || snapshot);
              setIsAdding(true);
            }
          }}
          selectedItemId={selectedItemId}
          onSelectedItemChange={setSelectedItemId}
          editItemId={editItemId}
          onEditComplete={() => setEditItemId(null)}
          initialMediaId={
            renderSnapshot.media.find(
              (item) =>
                item.id === initialMediaId || item.url === initialMediaId
            )?.id || initialMediaId
          }
        />
        {isDraft ? (
          // Typed feedback stays visible after the last pin is removed, so the
          // unsent-changes guard never protects text the reviewer cannot see.
          (hasItems || value.generalFeedback.trim().length > 0) && (
            <div className="flex flex-col gap-3">
              {hasItems && (
                <MarkupList
                  title="Unsent annotations"
                  value={value}
                  selectedItemId={selectedItemId}
                  onSelect={setSelectedItemId}
                  onEdit={(id) => setEditItemId(id)}
                  onRemove={(id) => {
                    setValue((previous) => ({
                      ...previous,
                      annotations: previous.annotations.filter(
                        (item) => item.id !== id
                      ),
                      highlights: previous.highlights.filter(
                        (item) => item.id !== id
                      ),
                    }));
                    if (selectedItemId === id) setSelectedItemId(null);
                  }}
                  disabled={sending}
                />
              )}
              <CollaborationInputLazy
                ariaLabel="Annotation feedback"
                placeholder="Enter feedback"
                collaborators={adapter.collaborators}
                disabled={sending}
                isExternal={!sendInternally}
                isError={Boolean(sendError)}
                value={value.generalFeedback}
                onChange={(generalFeedback) =>
                  setValue((previous) => ({ ...previous, generalFeedback }))
                }
                onSubmit={() => void send()}
                className="w-full shrink-0 border-neutral-100 bg-white text-sm leading-5"
              />
              {sendError && (
                <p role="alert" className="m-0 text-red-600">
                  {sendError}
                </p>
              )}
              <div className="flex flex-wrap items-center justify-between gap-2">
                <label className="flex items-center gap-2 text-xs leading-4">
                  <Switch
                    size="sm"
                    checked={sendInternally}
                    onCheckedChange={setInternal}
                    disabled={sending || !canSendExternally}
                    aria-label="Send internally"
                  />
                  <span>
                    Send {sendInternally ? "internally" : "externally"} as{" "}
                    <strong className="font-semibold">{reviewerName}</strong>
                  </span>
                </label>
                <div className="ml-auto flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="rounded-lg"
                    disabled={sending}
                    onClick={() => requestLeave(resetDraft)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    className="rounded-lg"
                    disabled={
                      !canWrite ||
                      mediaNeedsSaving ||
                      sending ||
                      hasPendingPin ||
                      !hasItems
                    }
                    onClick={() => void send()}
                  >
                    {sending ? "Sending…" : "Send"}
                  </Button>
                </div>
              </div>
            </div>
          )
        ) : (
          <>
            {selectedComment?.body && (
              <div className="rounded-lg bg-blue-37 p-3">
                <div className="mb-2 flex items-center gap-2 font-semibold">
                  Feedback
                  {selectedComment.metadata?.isAiSummary && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          className="!size-4 !min-w-0 !p-0 border-0 bg-transparent text-xs text-blue-500"
                          aria-label="Summary of annotations is generated by AI."
                        >
                          <i
                            className="fa-regular fa-sparkles"
                            aria-hidden="true"
                          />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        Summary of annotations is generated by AI.
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
                <div className="whitespace-pre-wrap break-words [&_p]:font-normal">
                  <UserMessageContentV2
                    comment={selectedComment}
                    collaborators={adapter.collaborators}
                  />
                </div>
              </div>
            )}
            <MarkupList
              value={current}
              selectedItemId={selectedItemId}
              onSelect={setSelectedItemId}
              resolution={selectedComment?.metadata?.markupResolution}
              onResolve={
                canWrite && selectedComment
                  ? (resolved) => void resolve(selectedComment, resolved)
                  : undefined
              }
              disabled={isResolving}
            />
          </>
        )}
      </div>
    );
  };
  const renderActivity = (variant: "modal" | "drawer" = "drawer") =>
    isNewPost ? (
      <div className="p-6 text-center text-sm">
        You can add comments to your post or draft after it’s created.
      </div>
    ) : (
      <ActivityPane
        adapter={adapter}
        channelId={channelId}
        variant={variant}
        containers={activityContainers}
        onVisibilityChange={onActivityVisibilityChange}
      />
    );
  const controller: PostMarkupController = enabled
    ? {
        enabled,
        canWrite,
        activeTab,
        setActiveTab: changeTab,
        hasUnsentChanges,
        requestLeave,
        openMarkup,
        renderMarkup,
        renderActivity,
        post,
        resolve,
        isResolving,
      }
    : {
        enabled: false,
        canWrite: false,
        activeTab: initialTab,
        setActiveTab: () => {},
        hasUnsentChanges,
        requestLeave,
        openMarkup: () => {},
        renderMarkup: () => null,
        renderActivity: () => null,
        post,
        resolve: async () => {},
        isResolving: false,
      };
  return (
    <PostMarkupContext.Provider value={controller}>
      {children}
      <Dialog
        open={Boolean(pendingLeave)}
        onOpenChange={(open) => {
          if (!open) setPendingLeave(null);
        }}
      >
        <DialogContent
          showCloseButton={false}
          contentWrapperMode="custom"
          contentWrapperClassName="gap-6"
          className="max-w-[520px] rounded-2xl ring-1 ring-inset ring-neutral-100 p-6 font-inter font-normal"
          onEscapeKeyDown={(event) => event.stopPropagation()}
        >
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="grid size-10 place-items-center rounded-lg bg-yellow-50">
              <i
                aria-hidden="true"
                className="fa-regular fa-triangle-exclamation text-red-600"
              />
            </div>
            <div>
              <DialogTitle className="font-inter text-2xl font-bold leading-8 text-neutral-800">
                Discard unsent annotations?
              </DialogTitle>
              <DialogDescription className="mb-0 mt-2 text-base leading-6 text-neutral-800">
                You will lose your unsent pins and caption highlights.
              </DialogDescription>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              className="h-10 rounded-lg"
              onClick={() => setPendingLeave(null)}
            >
              Keep editing
            </Button>
            <Button
              type="button"
              className="h-10 rounded-lg"
              onClick={() => {
                const action = pendingLeave;
                setPendingLeave(null);
                resetDraft();
                action?.();
              }}
            >
              Discard annotations
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </PostMarkupContext.Provider>
  );
}
